import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '@/store';
import { useSession } from '@/hooks/useSession';
import { getGuestUser, setGuestUsername } from '../../apiServices/projectApi';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import {
  setItems,
  setMessages,
  setProjectMetadata,
  setTasks,
  updateProjectUsers,
  updateUserRole,
  addUser,
} from '../../app/features/project/projectSlice';
import { setItemsForParent } from '@/store/itemsSlice';
import { transformItems } from '../../app/features/project/utils';
import { setGuestUser } from '../../app/features/user/userSlice';
import { MainViewLoading } from '../project/components/MainViewLoading';
import { ProjectMainView } from '../project/ProjectMainView';

export default function GuestProjectPage() {
  const { id: guestHash } = useParams<{ id: string }>();
  const { isAuthenticated, handleLogout, updateCurrentItem } = useSession();
  const dispatch = useDispatch<AppDispatch>();

  const { details, title } = useSelector((state: RootState) => state.project);
  const user = useSelector((state: RootState) => state.user);

  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [initFirstname, setInitFirstname] = useState('');
  const [initLastname, setInitLastname] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // If user is logged in, log them out since they are at guest page
  useEffect(() => {
    if (isAuthenticated) handleLogout(true);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!guestHash) return;

    getGuestUser(guestHash)
      .then((res: any) => {
        const [userR, usersR, projectR, messagesR, tasksR, resourcesR] = res;

        if (userR.status === 'rejected') {
          throw new userR.reason();
        }

        dispatch(updateUserRole('guest'));

        if (userR.status === 'fulfilled') {
          dispatch(setGuestUser(userR.value.response.Item));
        }
        if (usersR.status === 'fulfilled') {
          dispatch(updateProjectUsers(usersR.value.response.Items));
        }
        if (projectR.status === 'fulfilled') {
          dispatch(
            setProjectMetadata({
              ...projectR.value.response.Item,
              guestHash,
            }),
          );
        }
        if (messagesR.status === 'fulfilled') {
          dispatch(setMessages(messagesR.value.response.Items));
        }
        if (tasksR.status === 'fulfilled') {
          dispatch(setTasks(tasksR.value.response.Items));
        }
        if (resourcesR.status === 'fulfilled') {
          dispatch(setItems(resourcesR.value.response.Items));
          // Also write to global itemsSlice so ProjectMainView can read them
          const realProjectId = projectR.status === 'fulfilled' ? projectR.value.response.Item?.id?.S : undefined;
          if (realProjectId) {
            const normalized = transformItems(resourcesR.value.response.Items || []);
            dispatch(setItemsForParent({ parentId: realProjectId, items: normalized }));
          }
        }

        updateCurrentItem(details);
        setLoading(false);
      })
      .catch((err: unknown) => {
        console.error(err);
        setError('Link is invalid');
        setLoading(false);
      });
  }, []);

  const submitUserDetails = async () => {
    if (!guestHash || !initFirstname.length || !initLastname.length || submitting) return;

    setSubmitting(true);
    const res = await setGuestUsername(guestHash, initFirstname, initLastname);

    if (res.response?.Item) {
      const newUser = res.response.Item;
      dispatch(
        addUser({
          given_name: newUser.firstname,
          family_name: newUser.lastname,
          uid: user.uid,
          email: newUser.email,
          role: 'guest',
        }),
      );
      dispatch(setGuestUser(res.response.Item));
    }
    setSubmitting(false);
  };

  const actualProjectId = details?.id ?? null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <MainViewLoading previewListItemsAmount={13} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <h1 className="text-xl text-accent-foreground">{error}</h1>
      </div>
    );
  }

  // User visits page first time — needs to enter name
  if (!user.given_name) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay">
        <div className="bg-card rounded-lg p-8 max-w-md w-full mx-4 space-y-5">
          <h2 className="text-lg font-medium text-foreground">
            Please add your name below and you're ready to join the space {title}
          </h2>
          <div className="space-y-1">
            <label htmlFor="firstname" className="text-sm font-medium text-accent-foreground">
              First name
            </label>
            <Input
              id="firstname"
              value={initFirstname}
              onChange={(e) => setInitFirstname(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="lastname" className="text-sm font-medium text-accent-foreground">
              Last name
            </label>
            <Input
              id="lastname"
              value={initLastname}
              onChange={(e) => setInitLastname(e.target.value)}
            />
          </div>
          <Button
            onClick={submitUserDetails}
            disabled={submitting || !initFirstname.length || !initLastname.length}
            className="w-full"
          >
            {submitting ? <Spinner size="sm" /> : 'Submit'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-xl font-semibold px-4 py-3">{title}</h1>
      <ProjectMainView
        currentUserGroup={details?.group ?? null}
        projectId={actualProjectId}
      />
    </div>
  );
}
